<script setup>
  import {computed, ref} from "vue";

  import ComponentAiRating from "@components/AiRating.vue";
  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import PagesRecordResultListClaimTextBox from "@pages/Records/Components/Result/ResultList/ClaimTextBox.vue";
  import {MedButton, MedInfoButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {extractAiErrorMessage} from "@utils/Helpers/AiErrorMessage.js";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "PagesRecordResultPatientTranslateDialog",
  });

  const emit = defineEmits(["accept", "open:coreInfoDialog"]);
  const refPatientTranslateDialog = ref(null);

  // input properties
  const patientId = ref(null);
  const recordId = ref(null);
  const claim = ref(null);
  const mcCheckId = ref(null);
  const isStatic = ref(false);

  // generated texts
  const problemtext = ref("");
  const solution = ref("");
  const error = ref(null);

  const hasGeneratedResult = computed(() => !error.value && (problemtext.value || solution.value));
  const isLoading = ref(false);

  const rating = ref(null);
  const ratingReason = ref(null);

  // kept so we can cancel the request while it's still loading
  let request = null;

  const generate = async () => {
    request?.cancel();

    updateResponse(null);
    reset();

    isLoading.value = true;

    request = useAuthHttp();
    request.get(
      route("api.claims.patient-translate", {
        patient: patientId.value,
        record: recordId.value,
        check: claim.value.check_id,
        claim: claim.value.id,
      }),
      {
        onSuccess: (data) => {
          if (data.debug) {
            console.info(data.debug);
          }

          problemtext.value = data.problemtext.replaceAll("\n", "<br>");
          solution.value = data.solution.replaceAll("\n", "<br>");
        },

        onError: (errors) => (error.value = Object.values(errors).join("\n")),
        onNetworkError: (error_) => (error.value = error_.message),

        onHttpException: (response) =>
          (error.value = extractAiErrorMessage(
            response.data,
            "Es ist ein Fehler bei der Umformulierung aufgetreten. Bitte versuchen Sie es später erneut.",
          )),

        onFinish: () => (isLoading.value = false),
        onCancel: () => (isLoading.value = false),
      },
    );
  };

  const updateResponse = (actionValue) => {
    if (!hasGeneratedResult.value || (!actionValue && !rating.value)) {
      // nothing to update - skip
      return;
    }

    useAuthHttp({
      rating: rating.value,
      ratingReason: ratingReason.value,
      action: actionValue,
    }).post(
      route("api.claims.patient-translate.update", {
        patient: patientId.value,
        record: recordId.value,
        check: claim.value.check_id,
        claim: claim.value.id,
      }),
    );
  };

  const reset = () => {
    problemtext.value = "";
    solution.value = "";
    error.value = null;
    rating.value = null;
    ratingReason.value = null;
  };

  const open = (newPatientId, newRecordId, newClaim, newMcCheckId, newIsStatic) => {
    patientId.value = newPatientId;
    recordId.value = newRecordId;
    claim.value = newClaim;
    mcCheckId.value = newMcCheckId;
    isStatic.value = newIsStatic;

    reset();
    refPatientTranslateDialog.value.open();
    generate();
  };

  const apply = () => {
    emit("accept", {
      problemtext: problemtext.value,
      solution: solution.value,
    });

    close(true);
  };

  const close = (wasAccepted = false) => {
    const wasLoading = isLoading.value;

    request?.cancel();
    isLoading.value = false;

    // cancelling mid-load: nothing was generated yet, so there's nothing to tell the backend
    if (wasAccepted || !wasLoading) {
      updateResponse(wasAccepted ? "accept" : "abort");
    }

    refPatientTranslateDialog.value?.close();
  };

  defineExpose({
    open,
  });
</script>

<template>
  <ComponentDialog
    ref="refPatientTranslateDialog"
    dialog-width="large"
    :test-id="getTestId('patient-translate-dialog')"
  >
    <template #content>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h1 class="mb-3 text-2xl">Originaltext</h1>

          <PagesRecordResultListClaimTextBox
            :read-only="true"
            :solution="claim.solution"
            :problem="claim.problem"
            :comment="claim.comment"
            :problem-original="claim.problem"
            :solution-original="claim.solution"
            :mc-check-id="mcCheckId"
            :allow-shortening="false"
            @open:core-info-dialog="(data) => $emit('open:coreInfoDialog', data)"
          />
        </div>

        <div>
          <h1 class="mb-3 text-2xl">
            <template v-if="isStatic">
              Text in laiengerechter Sprache
              <MedInfoButton
                size="lg"
                side="top"
                aria-label="Laientext Info"
                disable-portal
                :data-test="getTestId('patient-translate-layman-info-button')"
              >
                <p class="text-sm">Der Originaltext wurde vom MediCheck Team in laiengerechte Sprache übersetzt.</p>
              </MedInfoButton>
            </template>
            <template v-else>
              KI-Text in laiengerechter Sprache
              <MedInfoButton
                size="lg"
                side="top"
                aria-label="KI-Text Info"
                disable-portal
                :data-test="getTestId('patient-translate-ai-info-button')"
              >
                <p class="text-sm">
                  Mithilfe dieser KI-basierten Funktion können Texte für den Patientenbericht in eine leicht
                  verständliche Sprache umformuliert werden. Quelle für die Umformulierung ist der Originaltext des
                  MediCheck. Der Schutz Ihrer Angaben und der darin enthaltenen Informationen ist jederzeit
                  gewährleistet.
                </p>
                <p class="mt-4 text-sm">
                  Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Nutzen Sie
                  daher den Originaltext des MediCheck als Ausgangspunkt und überprüfen Sie alle Fakten und wichtigen
                  Informationen sorgfältig.
                </p>
              </MedInfoButton>
            </template>
          </h1>

          <template v-if="isLoading">
            <div class="my-10 flex justify-center">
              <MedSpinner size="3xl" :data-test="getTestId('patient-translate-dialog-spinner')" />
            </div>
          </template>

          <template v-else-if="error">
            <ComponentAlert type="error">
              {{ error }}
            </ComponentAlert>
          </template>

          <div v-else-if="hasGeneratedResult">
            <PagesRecordResultListClaimTextBox
              :read-only="true"
              :solution="solution"
              :problem="problemtext"
              :problem-original="claim.problem"
              :solution-original="claim.solution"
              :mc-check-id="mcCheckId"
              :allow-shortening="false"
            />
            <p v-if="!isStatic" class="mt-2 text-sm text-gray-500">
              KI-generierte Inhalte können Fehler enthalten. Bitte überprüfen Sie wichtige Informationen.
            </p>
          </div>

          <div v-if="hasGeneratedResult" class="mt-3">
            <MedButton
              v-if="!isStatic"
              icon="cached"
              :data-test="getTestId('patient-translate-dialog-generate-button')"
              @click="generate"
            >
              Neu generieren
            </MedButton>

            <ComponentAiRating
              v-model:rating="rating"
              v-model:reason="ratingReason"
              label="War diese Umformulierung in laiengerechte Sprache hilfreich für Sie?"
            />
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :data-test="getTestId('patient-translate-dialog-apply-button')"
        :disabled="!hasGeneratedResult || isLoading"
        @click="apply"
      >
        Übernehmen
      </MedButton>

      <MedButton :data-test="getTestId('patient-translate-dialog-cancel-button')" @click="() => close()">
        Abbrechen
      </MedButton>
    </template>
  </ComponentDialog>
</template>
